import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";

import { CSSProvider } from "@perfectserve/css-styles-react";
import env from "@perfectserve/env-ps";
import { HttpProvider, useHttpContext } from "@perfectserve/http-ps";
import { ToastProvider } from "@perfectserve/library";

import Navigation from "./components/Navigation";
import { UserSelector } from "./components/UserSelector";
import { AppProvider } from "./contexts/AppProvider";
import { useTransition } from "./contexts/TransitionContext";
import { getEnvConfig } from "./hooks/useEnv";
import { authService } from "./services/authService";
import AppRouter from "./AppRouter";
import { useAppAuth, useUser } from "./hooks";

import "./App.css";
import "./components/components.css";

// Component to expose HTTP service globally
const GlobalHttpServiceSetup = () => {
  const { httpService } = useHttpContext();

  useEffect(() => {
    // Expose HTTP service globally for packages like Context Selector
    window.PS_HTTP_SERVICE = httpService;

    return () => {
      // Cleanup on unmount
      delete window.PS_HTTP_SERVICE;
    };
  }, [httpService]);

  return null; // This component doesn't render anything
};

// Main App Component
export const AppContent = () => {
  const { isAuthenticated, loading, isUserConfigurationReady, isPermissionsLoading } = useAppAuth();
  const { user: _user } = useUser();
  const { showRoleModal, setShowRoleModal, practitionerRole, careTeamRoles, setSelectedRole } = useUser();
  const { navigate } = useTransition();
  const location = useLocation();

  // Track navigation fade-in state
  const [navigationVisible, setNavigationVisible] = useState(false);

  // Hide navigation on landing page and callback page, and wait for userConfiguration and permissions
  const showNavigation =
    location.pathname !== "/" &&
    location.pathname !== "/index.html" &&
    isAuthenticated &&
    isUserConfigurationReady &&
    !isPermissionsLoading;

  // Handle navigation fade-in when permissions are loaded
  useEffect(() => {
    if (showNavigation && !navigationVisible) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setNavigationVisible(true);
      }, 50);
      return () => clearTimeout(timer);
    } else if (!showNavigation && navigationVisible) {
      setNavigationVisible(false);
    }
  }, [showNavigation, navigationVisible]);

  // Handle custom auth redirect events
  useEffect(() => {
    const handleAuthRedirect = (event: CustomEvent<string>) => {
      navigate(event.detail);
    };

    window.addEventListener("auth-redirect", handleAuthRedirect as EventListener);

    return () => {
      window.removeEventListener("auth-redirect", handleAuthRedirect as EventListener);
    };
  }, [navigate]);

  const handleRoleSelect = async (role: any) => {
    await setSelectedRole(role);
    setShowRoleModal(false);
  };

  useEffect(() => {
    const pendoKey = env.pendoApiKey;

    if (!pendoKey) {
      console.warn("Pendo API key not configured, skipping Pendo initialization");
      return;
    }

    const s = document.createElement("script");
    s.src = `https://content.pendo.perfectserve.com/agent/static/${pendoKey}/pendo.js`;
    s.async = true;
    document.body.appendChild(s);

    return () => {
      document.body.removeChild(s);
    };
  }, []);

  // Combine all roles for the UserSelector
  const allRoles = [...(practitionerRole ? [practitionerRole] : []), ...careTeamRoles];

  // Show loading screen if still loading or waiting for userConfiguration
  // Note: Don't block on permissions loading - allow app to work with empty permissions
  if (loading || (isAuthenticated && !isUserConfigurationReady && !showRoleModal)) {
    return <div className="app-loading-container" />;
  }

  return (
    <div className="App">
      {/* Role Selection Modal - show over everything when active */}
      <UserSelector roles={allRoles} selectedRole={null} onRoleChange={handleRoleSelect} isOpen={showRoleModal} />

      {/* Main app content - only show when NOT in role selection */}
      {!showRoleModal && (
        <>
          {showNavigation && <Navigation isVisible={navigationVisible} />}
          <main>
            <div className="content-wrapper">
              <AppRouter />
            </div>
          </main>
        </>
      )}
    </div>
  );
};

const ErrorFallback = () => (
  <div className="app-error-container">
    <h1 className="app-error-title">Something went wrong</h1>
    <p className="app-error-message">An unexpected error occurred. Our team has been notified.</p>
    <button type="button" onClick={() => window.location.reload()} className="app-error-button">
      Reload Page
    </button>
  </div>
);

export const App = () => {
  // Auth error handler for HTTP service
  const handleAuthError = (_error: any) => {
    // Clear stored auth data and trigger logout
    localStorage.removeItem("ps_auth");
    localStorage.removeItem("ps_userConfiguration");
    authService.logout();
  };

  return (
    <CSSProvider>
      <ToastProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <HttpProvider
            getAuthToken={() => authService.getAccessToken()}
            envConfig={getEnvConfig() || {}}
            onAuthError={handleAuthError}
            defaultTimeout={30000}
          >
            <GlobalHttpServiceSetup />
            <AppProvider>
              <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
                <AppContent />
              </Sentry.ErrorBoundary>
            </AppProvider>
          </HttpProvider>
        </Router>
      </ToastProvider>
    </CSSProvider>
  );
};

export default App;
